import { useEffect, useRef, useState, useCallback } from "react";
import { Wifi, WifiOff, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * 149-cw-r154 — HealthCheckWidget.
 *
 * Indikator koneksi kecil di topbar. Poll `/api/health` setiap 30s.
 * Status:
 *  - `ok`           → hijau, "Server OK"
 *  - `checking`     → abu-abu, "Checking…" (first check)
 *  - `reconnecting` → amber, "Reconnecting…" (response gagal / timeout)
 *  - `offline`      → merah, "Offline" (navigator.onLine=false)
 *
 * Klik widget → force re-check.
 * Auto re-check on `online` event supaya user tahu segera setelah kembali online.
 *
 * Testid: `health-check-widget`, atribut `data-status` untuk assertion.
 */
const CFG = {
  ok:           { ring: "border-emerald-300 bg-emerald-50 text-emerald-800", icon: (s) => <Wifi size={s} />,             label: "Server OK" },
  checking:     { ring: "border-slate-300 bg-slate-50 text-slate-600",       icon: (s) => <Loader2 size={s} className="animate-spin" />, label: "Checking" },
  reconnecting: { ring: "border-amber-300 bg-amber-50 text-amber-800",       icon: (s) => <Loader2 size={s} className="animate-spin" />, label: "Reconnect" },
  offline:      { ring: "border-rose-300 bg-rose-50 text-rose-800",          icon: (s) => <WifiOff size={s} />,           label: "Offline" },
};

export default function HealthCheckWidget({ intervalMs = 30000 }) {
  const [status, setStatus] = useState("checking");
  const [lastOk, setLastOk] = useState(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setStatus("offline");
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await api.get("/health", { timeout: 8000 });
      setStatus("ok");
      setLastOk(Date.now());
    } catch (e) {
      // Kalau 401 (session invalid), tetap treat as reconnecting sebab health
      // endpoint dianggap public — kalau 401 berarti axios interceptor / server issue.
      setStatus("reconnecting");
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, intervalMs);
    const onOnline = () => check();
    const onOffline = () => setStatus("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [check, intervalMs]);

  const cfg = CFG[status] || CFG.checking;
  const title = status === "ok" && lastOk
    ? `Server OK · terakhir dicek ${new Date(lastOk).toLocaleTimeString("id-ID")}`
    : `Status: ${cfg.label} · klik utk cek ulang`;

  return (
    <button
      type="button"
      onClick={check}
      title={title}
      data-testid="health-check-widget"
      data-status={status}
      className={`inline-flex items-center gap-1.5 h-9 px-2.5 border ${cfg.ring} text-[10px] uppercase tracking-wider font-semibold hover:opacity-80 transition-opacity`}
    >
      {cfg.icon(12)}
      <span className="hidden lg:inline">{cfg.label}</span>
    </button>
  );
}
